import { useState } from 'react'
import {
  CurecallButton,
  CurecallPasswordInput,
  CurecallModalContainer,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { TwoFactorService } from 'src/services/two-factor.service'

type Props = {
  isOpen: boolean
  onClose: () => void
  onDisabled: () => void
}

export default function TwoFactorDisableModal({ isOpen, onClose, onDisabled }: Props) {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDisable = async () => {
    setIsLoading(true)
    setError(null)
    try {
      await TwoFactorService.disable(password)
      notify({
        title: t('common.actions.success'),
        message: t('pages.auth.account.two-factor.disabled-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
      onDisabled()
      onClose()
    } catch {
      setError(t('pages.auth.account.two-factor.wrong-password'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      title={t('pages.auth.account.two-factor.disable-title')}
      isOpen={isOpen}
      toggle={onClose}
      dialogClassName="sm:c-w-[450px]"
    >
      <div className="c-flex c-flex-col c-gap-6">
        <p className="c-text-sm c-text-gray-600">{t('pages.auth.account.two-factor.disable-description')}</p>

        <CurecallPasswordInput
          label={t('pages.auth.input.password')}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            setError(null)
          }}
          errorMessage={error ?? undefined}
          isRequired
          autoFocus
        />

        <div className="c-flex c-justify-center c-gap-4">
          <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onClose}>
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton onClick={handleDisable} isLoading={isLoading} isDisabled={!password}>
            {t('pages.auth.account.two-factor.disable-confirm')}
          </CurecallButton>
        </div>
      </div>
    </CurecallModalContainer>
  )
}
